import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useCountries } from "../../api/queries/enum/useCountries";
import { useCountryStates } from "../../api/queries/enum/useCountryStates";
import { Panel } from "primereact/panel";
import { usePlacementEnvironmentAddInstitution } from "../../api/queries/placement-environment/usePlacementEnvironmentAddInstitution";
import { PlacementEnvironmentAddInstitutionPayload } from "../../api/types/placement-environment";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { Institution } from "../../api/types/institution";
import { useDebounce } from "../../common/useDebounce";
import { useInstitutionsSearch } from "../../api/queries/institutions/useInstitutionsSearch";
import commonStyles from "../common.module.css";

export default function PlacementEnvironmentInstitutionForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const addInstituon = usePlacementEnvironmentAddInstitution(uuid);
  const { data: countryOptions } = useCountries();

  const [id, setId] = useState<null | string>(null);

  const [name, setName] = useState<string>("");
  const onNameChange = (event: AutoCompleteChangeEvent): void => {
    if (typeof event.value === "string") {
      setName(event.value);
      setId(null);
    } else {
      const institution: Institution = event.value;
      setId(institution.id);
      setName(institution.name);
      setCountry(institution.country?.id || "");
      setCity(institution.city);
      setState(institution.country_state?.id || "");
      setWeb(institution.web);
    }
  };
  const nameDebounced = useDebounce(name, 500);

  const { data: institutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 5,
    search: nameDebounced,
  });
  const [institutionOptions, setInstitutionOptions] = useState<Institution[]>(
    [],
  );

  useEffect(() => {
    if (institutionsResponse) {
      setInstitutionOptions(institutionsResponse.data.results);
    }
  }, [institutionsResponse]);

  const onInstitutionSearchComplete = () => {};

  const [web, setWeb] = useState<string>("https://");
  const handleWebChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setWeb(event.target.value);
  };

  const [country, setCountry] = useState<string>("");
  const onCountryChange = (event: DropdownChangeEvent) => {
    setCountry(event.value);
    setState("");
  };

  const { data: countryStateOptions } = useCountryStates({
    limit: 100,
    offset: 0,
    fCountryId: country,
  });

  const [state, setState] = useState<string>("");
  const onStateChange = (event: DropdownChangeEvent) => {
    setState(event.value);
  };

  const [city, setCity] = useState<string>("");
  const onCityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const payload: PlacementEnvironmentAddInstitutionPayload = {
      institution: id,
      name,
      web,
      country,
      state: state || null,
      city,
    };

    await addInstituon.mutateAsync(payload);

    queryClient.invalidateQueries(queryKeys.institutionList(), {
      refetchInactive: true,
    });

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid), {
      refetchInactive: true,
    });

    navigate(`/journey-trackers/${uuid}`);
  };

  const onCancel = () => navigate(`/journey-trackers/${uuid}`);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <div className="p-fluid">
        <Panel header="General" toggleable={true} className="mb-3">
          <div className="field pt-3">
            <div className="p-float-label">
              <AutoComplete
                value={name}
                suggestions={institutionOptions}
                completeMethod={onInstitutionSearchComplete}
                onChange={onNameChange}
                field="name"
              />
              <label>Institution name *</label>
            </div>
          </div>

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <Dropdown
                  options={countryOptions?.data.results || []}
                  value={country}
                  onChange={onCountryChange}
                  optionLabel="name"
                  optionValue="id"
                />
                <label>Country *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <InputText value={city} onChange={onCityChange} />
                <label>City *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <Dropdown
                  options={countryStateOptions?.data.results || []}
                  value={state}
                  onChange={onStateChange}
                  optionLabel="name"
                  optionValue="id"
                  disabled={!country}
                />
                <label>State *</label>
              </div>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={web} onChange={handleWebChange} />
              <label>Web *</label>
            </div>
            <small className={commonStyles.fieldHelp}>
              Web address starting with https://
            </small>
          </div>
        </Panel>
      </div>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-sm p-button-secondary"
          text={true}
          onClick={onCancel}
        />

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!name || !country || !city || !web}
        />
      </div>
    </form>
  );
}
